/* iter55u — Two pack-ladder editor cards extracted from PricingPanel.jsx.
 *
 * Both cards share the same "list of rows with id/name/tokens/price/desc"
 * layout, but the Copyscape variant has an extra "mark best" toggle
 * column and a distinct empty state.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { Field } from './_shared';

// 2026-08 — Claude reference COGS, used only to show admins a live
// margin estimate while editing token packs (previously several packs
// drifted into a loss with nothing in the UI to flag it — see
// /app/memory/UNIT_ECONOMICS_ANALYSIS_ROUND3.md). Matches
// `budget_service.TOKENS_PER_USD_REFERENCE` (60,000 tok/$ == $16.67/1M).
const CLAUDE_COGS_PER_1M_USD = 16.67;

function MarginBadge({ tokens, price }) {
  const cogs = (Number(tokens) || 0) / 1_000_000 * CLAUDE_COGS_PER_1M_USD;
  const p = Number(price) || 0;
  if (!p || !cogs) return null;
  const marginPct = ((p - cogs) / p) * 100;
  const healthy = marginPct >= 15;
  return (
    <span
      className={`rounded-sm px-1.5 py-0.5 text-[10px] font-mono-stat ${healthy ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}
      title={`Claude COGS ≈ $${cogs.toFixed(2)} at $${CLAUDE_COGS_PER_1M_USD}/1M tokens`}
    >
      {marginPct >= 0 ? `+${marginPct.toFixed(1)}%` : `${marginPct.toFixed(1)}%`} margin
    </span>
  );
}

/** Generic token-packs card (the primary pricing ladder). */
export function TokenPacksCard({ pricing, updateRow, addRow, removeRow }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="text-[12.5px] font-semibold mb-2">Token packs</div>
      <p className="text-[11px] text-muted-foreground mb-2">
        Margin badge estimates profit vs. Claude's ${CLAUDE_COGS_PER_1M_USD}/1M-token reference cost — a rough
        floor, since cheaper models (Gemini Flash, DeepSeek) cost less and raise real margin further.
      </p>
      <div className="space-y-2">
        {pricing.map((p, i) => (
          <div key={p._uid || i} className="grid grid-cols-12 items-end gap-2" data-testid={`admin-pricing-row-${i}`}>
            <Field label="ID" className="col-span-2">
              <Input value={p.id} onChange={(e) => updateRow(i, { id: e.target.value })} className="h-8 font-mono-stat text-[11.5px]" />
            </Field>
            <Field label="Name" className="col-span-3">
              <Input value={p.name || ''} onChange={(e) => updateRow(i, { name: e.target.value })} className="h-8 text-[12px]" />
            </Field>
            <Field label="Tokens" className="col-span-2">
              <Input type="number" value={p.tokens || 0} onChange={(e) => updateRow(i, { tokens: parseInt(e.target.value, 10) || 0 })} className="h-8 font-mono-stat text-[12px]" />
            </Field>
            <Field label="Price USD" className="col-span-2">
              <Input type="number" step="0.01" value={p.price || 0} onChange={(e) => updateRow(i, { price: parseFloat(e.target.value) || 0 })} className="h-8 font-mono-stat text-[12px]" />
            </Field>
            <Field label="Description" className="col-span-2">
              <Input value={p.desc || ''} onChange={(e) => updateRow(i, { desc: e.target.value })} className="h-8 text-[12px]" />
            </Field>
            <div className="col-span-1 flex items-center justify-end gap-1 pb-1">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeRow(i)} data-testid={`admin-pricing-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="col-span-12 -mt-1" data-testid={`admin-pricing-margin-${i}`}>
              <MarginBadge tokens={p.tokens} price={p.price} />
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={addRow} data-testid="admin-pricing-add">
        <Plus className="h-3.5 w-3.5" /> Add pack
      </Button>
    </div>
  );
}

/** iter55p — Copyscape token-pack ladder. */
export function CopyscapePacksCard({ csPacks, updateCsRow, addCsRow, removeCsRow, markCsBest }) {
  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="admin-copyscape-packs-card">
      <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
        Copyscape token packs
        <span className="rounded-sm bg-emerald-100 px-1.5 py-0 text-[10px] font-mono-stat text-emerald-700">plagiarism scans</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">
        Sold in the Buy Tokens dialog under the &ldquo;Copyscape scans&rdquo; tab. Each scan consumes 1 token from the buyer&apos;s Copyscape wallet. Mark exactly one pack as <b>Best value</b> to feature it.
      </p>
      <div className="space-y-2">
        {csPacks.map((p, i) => (
          <div key={p._uid || i} className="grid grid-cols-12 items-end gap-2" data-testid={`admin-copyscape-row-${i}`}>
            <Field label="ID" className="col-span-2">
              <Input value={p.id} onChange={(e) => updateCsRow(i, { id: e.target.value })} className="h-8 font-mono-stat text-[11.5px]" data-testid={`admin-copyscape-id-${i}`} />
            </Field>
            <Field label="Name" className="col-span-3">
              <Input value={p.name || ''} onChange={(e) => updateCsRow(i, { name: e.target.value })} className="h-8 text-[12px]" />
            </Field>
            <Field label="Scans" className="col-span-1">
              <Input type="number" value={p.tokens || 0} onChange={(e) => updateCsRow(i, { tokens: parseInt(e.target.value, 10) || 0 })} className="h-8 font-mono-stat text-[12px]" data-testid={`admin-copyscape-tokens-${i}`} />
            </Field>
            <Field label="Price USD" className="col-span-2">
              <Input type="number" step="0.01" value={p.price || 0} onChange={(e) => updateCsRow(i, { price: parseFloat(e.target.value) || 0 })} className="h-8 font-mono-stat text-[12px]" data-testid={`admin-copyscape-price-${i}`} />
            </Field>
            <Field label="Description" className="col-span-3">
              <Input value={p.desc || ''} onChange={(e) => updateCsRow(i, { desc: e.target.value })} className="h-8 text-[12px]" />
            </Field>
            <div className="col-span-1 flex items-center justify-end gap-1 pb-1">
              <button
                type="button"
                onClick={() => markCsBest(i)}
                data-testid={`admin-copyscape-best-${i}`}
                className={
                  'h-7 rounded-sm px-2 text-[10.5px] font-mono-stat transition ' +
                  (p.best
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70')
                }
                title="Mark as best value"
              >
                {p.best ? 'BEST' : 'mark'}
              </button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeCsRow(i)} data-testid={`admin-copyscape-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {csPacks.length === 0 && (
          <div className="text-[11.5px] text-muted-foreground italic" data-testid="admin-copyscape-empty">
            No Copyscape packs configured. Users will see the empty-state on the Copyscape tab.
          </div>
        )}
      </div>
      <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={addCsRow} data-testid="admin-copyscape-add">
        <Plus className="h-3.5 w-3.5" /> Add Copyscape pack
      </Button>
    </div>
  );
}
